import {
  Node,
  NodeDataFolder,
  NodeDataFolderResponse,
  NodeDataPartlistEntry,
  NodeDataPartlistEntryResponse,
  Tree,
} from "@org-avp/avp-avengers-projectstructure-service-api";
import { traverse } from "@org-avp/avp-avengers-ui-shared-utils";
import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useMemo } from "react";
import { projectStructureApi } from "../apis/project-structure-service-api";
import { createCachedLookup } from "../utils/lookup.utils";

const pollingIntervalInMS = 5_000;

export interface ProjectStructureHelper {
  psTreeResponse: {
    data?: Tree;
    isLoading: boolean;
    error?: unknown;
  };

  nodeDataFoldersResponse: {
    data?: NodeDataFolderResponse;
    isLoading: boolean;
    error?: unknown;
  };

  nodeDataPartlistEntriesResponse: {
    data?: NodeDataPartlistEntryResponse;
    isLoading: boolean;
    error?: unknown;
  };

  partlistEntryIdKeys: Set<string>;

  createNodeDataFolderLookup(): Map<string, NodeDataFolder>;

  createNodeDataPartlistEntryLookup(): Map<string, NodeDataPartlistEntry>;

  removeNodesFromTree(nodeUUIDs: string[]): void;

  removeNodesResult: {
    isLoading: boolean;
    error?: unknown;
  };
}

export const useProjectStructureHelper = (projectId: string, subprojectId: string): ProjectStructureHelper => {
  const psTreeResponse = projectStructureApi.useGetProjectStructureTreeQuery([projectId, subprojectId], { pollingInterval: pollingIntervalInMS });

  const nodeDataFoldersResponse = projectStructureApi.useGetNodeDataFoldersQuery([projectId, subprojectId]);
  projectStructureApi.useGetUpdatedNodeDataFoldersQuery(
    nodeDataFoldersResponse.data ? [projectId, subprojectId, nodeDataFoldersResponse.data.lastModified] : skipToken,
    { pollingInterval: pollingIntervalInMS },
  );

  const nodeDataPartlistEntriesResponse = projectStructureApi.useGetNodeDataPartlistEntriesQuery([projectId, subprojectId]);
  projectStructureApi.useGetUpdatedNodeDataPartlistEntriesQuery(
    nodeDataPartlistEntriesResponse.data ? [projectId, subprojectId, nodeDataPartlistEntriesResponse.data.lastModified] : skipToken,
    { pollingInterval: pollingIntervalInMS },
  );

  const [updateTreeMutation, removeNodesResult] = projectStructureApi.useUpdateProjectStructureTreeMutation();
  const removeNodesFromTree = useCallback(
    (nodeUUIDs: string[]) => {
      const nodeFilter = (it: Node) => !nodeUUIDs.includes(it.uuid);
      const treeCopy: Tree = JSON.parse(JSON.stringify(psTreeResponse.data));
      treeCopy.children = treeCopy.children.filter(nodeFilter);
      const filterChildren = (node: Node) => node.children = node.children.filter(nodeFilter);
      traverse(treeCopy.children, filterChildren);

      updateTreeMutation([projectId, subprojectId, treeCopy, true]);
    },
    [projectId, psTreeResponse.data, subprojectId, updateTreeMutation],
  );

  return {
    psTreeResponse,
    nodeDataFoldersResponse,
    nodeDataPartlistEntriesResponse,
    partlistEntryIdKeys: useMemo(
      () => new Set(nodeDataPartlistEntriesResponse.data?.content.map((it) => it.partlistEntryId)),
      [nodeDataPartlistEntriesResponse.data?.content],
    ),
    createNodeDataFolderLookup: useCallback(
      () => cacheNodeDataFolderLookup(nodeDataFoldersResponse.data),
      [nodeDataFoldersResponse.data],
    ),
    createNodeDataPartlistEntryLookup: useCallback(
      () => cacheNodeDataPartlistEntryLookup(nodeDataPartlistEntriesResponse.data),
      [nodeDataPartlistEntriesResponse.data],
    ),
    removeNodesFromTree,
    removeNodesResult,
  };
};

const nodeDataFolderLookupCache = new WeakMap<NodeDataFolderResponse, Map<string, NodeDataFolder>>();
const cacheNodeDataFolderLookup = (nodeDataFolders: NodeDataFolderResponse | undefined) => {
  return createCachedLookup(
    nodeDataFolderLookupCache,
    nodeDataFolders,
    (it) => it.content,
    (it) => it.nodeUUID,
  );
};

const nodeDataPartlistEntryLookupCache = new WeakMap<NodeDataPartlistEntryResponse, Map<string, NodeDataPartlistEntry>>();
const cacheNodeDataPartlistEntryLookup = (nodeDataPartlistEntries: NodeDataPartlistEntryResponse | undefined) => {
  return createCachedLookup(
    nodeDataPartlistEntryLookupCache,
    nodeDataPartlistEntries,
    (it) => it.content,
    (it) => it.nodeUUID,
  );
};
